import type { Lesson } from './types'

/**
 * Madd (elongation) rules. Madd Tabee'i (the natural 2-count elongation)
 * was already introduced in the harakat module as the foundation; these are
 * the secondary (far'i) rules that extend it further under specific
 * conditions - all triggered by a madd letter meeting either a hamza or a
 * sukun.
 */
export const maddRulesLessons: Lesson[] = [
  {
    id: 'madd-muttasil',
    title: 'Madd Muttasil (Connected Madd)',
    summary: 'A madd letter followed by hamza in the same word - stretched 4-5 counts.',
    explanation: [
      'Madd Muttasil ("connected madd") happens when a madd letter (ا و ي) is immediately followed by a hamza (ء) within the same word.',
      'This is an obligatory madd - it must be elongated, typically for 4 or 5 counts (rather than the natural 2), because shortening it would blur the hamza that follows.',
    ],
    colorLegend: [{ color: 'madd', label: 'Elongated 4-5 counts (madd letter + hamza, same word)' }],
    examples: [
      { arabic: 'السَّمَاءِ', transliteration: 'as-samāʾ', meaning: 'the sky', highlights: [{ text: 'اء', color: 'madd' }] },
      { arabic: 'جَاءَ', transliteration: 'jāʾa', meaning: 'he came', highlights: [{ text: 'اء', color: 'madd' }] },
      { arabic: 'سُوءَ', transliteration: 'sūʾa', meaning: 'evil', highlights: [{ text: 'وء', color: 'madd' }] },
    ],
  },
  {
    id: 'madd-munfasil',
    title: 'Madd Munfasil (Separated Madd)',
    summary: 'A word ending in a madd letter, followed by a word starting with hamza.',
    explanation: [
      'Madd Munfasil ("separated madd") is the same idea as Madd Muttasil, but split across two words: a word ends with a madd letter, and the very next word begins with a hamza.',
      'Because the hamza is technically in a separate word, this madd is not strictly obligatory the way Madd Muttasil is - some styles of recitation shorten it - but in the Hafs ʿan ʿĀṣim reading most commonly used today, it is also elongated 4-5 counts, matching Madd Muttasil in practice.',
    ],
    colorLegend: [{ color: 'madd', label: 'Elongated 4-5 counts (madd letter, then hamza starts the next word)' }],
    examples: [
      { arabic: 'قُوا أَنْفُسَكُمْ', transliteration: 'qū anfusakum', meaning: 'protect yourselves', highlights: [{ text: 'وا أَ', color: 'madd' }] },
      { arabic: 'فِي أَنْفُسِكُمْ', transliteration: 'fī anfusikum', meaning: 'within yourselves', highlights: [{ text: 'ي أَ', color: 'madd' }] },
    ],
  },
  {
    id: 'madd-lazim',
    title: 'Madd Lazim (Necessary Madd)',
    summary: 'A madd letter followed by a sukun or shaddah - the longest madd, always 6 counts.',
    explanation: [
      'Madd Lazim ("necessary madd") happens when a madd letter is followed by a sukun or a shaddah within the same word - and unlike Madd \'Aarid (below), this sukun or shaddah is a permanent part of the word, present whether you stop there or keep reading.',
      'This is the longest and strictest of all the madd rules: always exactly 6 counts, with no shorter option.',
      'It is also the rule behind the long stretches heard at the start of certain surahs, on the disconnected letters (like ALM, or Ya-Sin) - several of those letter-names themselves contain a madd letter followed immediately by a sukun.',
    ],
    colorLegend: [{ color: 'madd', label: 'Elongated exactly 6 counts (madd letter + permanent sukun/shaddah)' }],
    examples: [
      { arabic: 'الطَّامَّةُ', transliteration: 'aṭ-ṭāmmah', meaning: 'the overwhelming calamity', highlights: [{ text: 'امَّ', color: 'madd' }] },
      { arabic: 'الْحَاقَّةُ', transliteration: 'al-ḥāqqah', meaning: 'the Inevitable', highlights: [{ text: 'اقَّ', color: 'madd' }] },
      { arabic: 'دَابَّةٍ', transliteration: 'dābbah', meaning: 'a creature', highlights: [{ text: 'ابَّ', color: 'madd' }] },
    ],
  },
  {
    id: 'madd-aarid',
    title: 'Madd \'Aarid lis-Sukoon (Madd from Stopping)',
    summary: 'Stopping at the end of an ayah on a madd letter - your choice of 2, 4, or 6 counts.',
    explanation: [
      'Madd \'Aarid lis-Sukoon ("madd arising from stopping") is different from the others in one key way: it only appears because you choose to stop reciting at that word. If you kept reading past it, there would be no madd rule here at all.',
      'It applies when the second-to-last letter of the word you are stopping on is a madd letter, and the last letter would normally carry a vowel but becomes a sukun simply because you stopped there.',
      'Unlike Madd Lazim, this one is flexible: the reciter may choose 2, 4, or 6 counts, as long as they are consistent within a single recitation.',
    ],
    colorLegend: [{ color: 'madd', label: 'Elongated 2, 4, or 6 counts, reciter\'s choice (only when stopping here)' }],
    examples: [
      { arabic: 'الرَّحِيمْ', transliteration: 'ar-raḥīm', meaning: '...the Merciful (stopping here)', highlights: [{ text: 'يمْ', color: 'madd' }] },
      { arabic: 'الْعَالَمِينْ', transliteration: 'al-ʿālamīn', meaning: '...of all the worlds (stopping here)', highlights: [{ text: 'ينْ', color: 'madd' }] },
      { arabic: 'نَسْتَعِينْ', transliteration: 'nastaʿīn', meaning: '...we seek help (stopping here)', highlights: [{ text: 'ينْ', color: 'madd' }] },
    ],
  },
]
